/**
 * A design drawn small, for a project card: its components and the lines
 * between them, from the same scene model the canvas draws (invariant 7),
 * without labels, ports or findings, scaled to fit.
 *
 * It draws the working draft, which is what the project looks like now. That
 * is a picture on screen and nothing more: no row points at it and nothing is
 * stored (D35), so invariant 1 is untouched.
 *
 * Only what the draft pins is fetched. Library versions are immutable, so each
 * is fetched once per page load however many cards pin it.
 */
import { memo, useEffect, useMemo, useState } from "react";
import { api, type LibraryVersion } from "../api/client";
import type { DesignDocument, GroupDefinitionDocument, TemplateDocument } from "../design/document";
import { symbolTransform } from "../render";
import { Glyph } from "../render/glyphs";
import { THEME } from "../render/theme";
import { buildScene, DEFAULT_VIEW } from "../scene";
import type { DefinitionEntry, SceneLibrary } from "../scene/types";
import { pinnedVersions } from "./library";

const NO_ANNOTATIONS = { labels: false, dimensions: false, fluidLines: true, airLines: true, notes: false, wash: "none" as const };

const versions = new Map<string, Promise<LibraryVersion | null>>();

function version(id: string): Promise<LibraryVersion | null> {
  let pending = versions.get(id);
  if (!pending) {
    // A version that cannot be read draws as the scene's fallback; forget the
    // failure so a later card can try again.
    pending = api.getLibraryVersion(id).catch(() => {
      versions.delete(id);
      return null;
    });
    versions.set(id, pending);
  }
  return pending;
}

async function loadThumbnail(designId: string): Promise<{ document: DesignDocument; library: SceneLibrary }> {
  const draft = await api.getDraft(designId);
  const document = draft.content as DesignDocument;
  const pinned = pinnedVersions(document);
  const loaded = await Promise.all([...pinned.templates, ...pinned.definitions].map(version));
  const templates: Record<string, TemplateDocument> = {};
  const definitions: Record<string, DefinitionEntry> = {};
  for (const entry of loaded) {
    if (!entry) continue;
    if (entry.kind === "templates") templates[entry.id] = entry.content as unknown as TemplateDocument;
    else if (entry.kind === "blocks") {
      definitions[entry.id] = { name: "", content: entry.content as unknown as GroupDefinitionDocument };
    }
  }
  return { document, library: { templates, definitions } };
}

type State = { status: "loading" } | { status: "empty" } | { status: "ready"; document: DesignDocument; library: SceneLibrary };

/** The draft of `designId`, or a quiet placeholder while loading, when empty, or if it cannot be read. */
export function DesignThumbnail({ designId }: { designId: string | null | undefined }) {
  const [state, setState] = useState<State>(designId ? { status: "loading" } : { status: "empty" });
  useEffect(() => {
    if (!designId) {
      setState({ status: "empty" });
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    loadThumbnail(designId)
      .then(({ document, library }) => {
        if (cancelled) return;
        const empty = (document.nodes ?? []).length === 0 && (document.group_instances ?? []).length === 0;
        setState(empty ? { status: "empty" } : { status: "ready", document, library });
      })
      .catch(() => !cancelled && setState({ status: "empty" }));
    return () => {
      cancelled = true;
    };
  }, [designId]);

  if (state.status !== "ready") {
    return <div className={`thumbnail-placeholder${state.status === "loading" ? " loading" : ""}`} aria-hidden="true" />;
  }
  return <DrawnDesign document={state.document} library={state.library} />;
}

const DrawnDesign = memo(function DrawnDesign({ document, library }: { document: DesignDocument; library: SceneLibrary }) {
  const scene = useMemo(
    () => buildScene(document, library, [], { ...DEFAULT_VIEW, annotations: NO_ANNOTATIONS }),
    [document, library],
  );
  const { minX, minY, maxX, maxY } = scene.bounds;
  const pad = 24;
  const viewBox = `${minX - pad} ${minY - pad} ${Math.max(1, maxX - minX) + 2 * pad} ${Math.max(1, maxY - minY) + 2 * pad}`;
  return (
    <svg className="thumbnail-drawing" viewBox={viewBox} preserveAspectRatio="xMidYMid meet" aria-hidden="true" focusable="false">
      {scene.edges.map((edge) => (
        <polyline
          key={edge.ref}
          points={edge.path.map((point) => `${point.x},${point.y}`).join(" ")}
          fill="none"
          stroke={THEME.channel}
          strokeWidth={1.5}
          strokeDasharray={edge.air ? "1 4" : undefined}
          strokeLinecap="round"
          // Screen pixels: a large design drawn small keeps visible lines.
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {scene.nodes.map((node) => (
        <g key={node.ref} transform={symbolTransform(node.centre, node.rotation, node.mirrored)}>
          <Glyph name={node.glyph} size={node.size} />
        </g>
      ))}
    </svg>
  );
});
